import { log, TagId } from '@ms-ows/logging'

import { overrideCustomizer } from '../customizations'
import {
  FabricDependencies,
  IButtonProps,
  ICustomizerProps,
  ITeachingBubbleProps,
  ITheme,
} from '../fabric-dependencies'
import {
  ActionType,
  ClickAction,
  ClickActionParams,
  IPageContext,
  SurfaceParams,
  SurfaceParamsProps,
  TargetElementInfo,
} from '../models'
import { handleClickAction } from '../office-web-surfaces/render/actions'
import { isValue } from '../utils'

import { getCoachmarkSurface } from './coachmark'
import { getTeachingSurface } from './teaching'

// needed to name component
export { ICustomizerProps }

type Props = SurfaceParams &
  TargetElementInfo &
  Pick<IPageContext, 'primaryColour' | 'secondaryColour'>

export type SurfaceProps = {
  focused: boolean
  onCalloutDismiss: IButtonProps['onClick']
  onPrimaryButtonClick: IButtonProps['onClick']
  onSecondaryButtonClick: IButtonProps['onClick']
  styles?: ITeachingBubbleProps['styles']
  targetElement: HTMLElement
  theme?: ITheme
  onFocused(): void
  onImageLoaded(imageLoaded: number): void
} & SurfaceParamsProps

const requiredPropKeys: (keyof Props)[] = [
  'description',
  'primaryButtonText',
  'selector',
  'surfaceType',
  'targetElement',
]

const verifyProps = (props: Props) => {
  const invalidKeys = requiredPropKeys.filter(k => !isValue(props[k]))
  if (invalidKeys.length) {
    log.error(
      0x23602884 /* tag_9yc8e */,
      'Invalid props passed to OfficeWebServiceContainer',
      ...invalidKeys
    )

    return false
  }

  return true
}

const handleClick = (
  tagId: TagId,
  button: ActionType,
  props: Props,
  setHidden: (hidden: boolean) => void,
  clickAction?: ClickAction,
  clickActionParams?: ClickActionParams,
  eventType?: string
) => () => {
  handleClickAction(
    tagId,
    button,
    props.selector,
    props.surfaceType,
    clickAction,
    clickActionParams,
    eventType
  )

  setHidden(true)
  props.onClosed && props.onClosed(button)
}

const onCalloutDismiss: (
  props: Props,
  setHidden: (hidden: boolean) => void
) => IButtonProps['onClick'] = (props, setHidden) => ev => {
  if (
    (ev?.currentTarget as Element)?.className?.indexOf(
      'ms-TeachingBubble-closebutton'
    ) !== -1
  ) {
    handleClick(
      log.reserve(0x23841607 /* tag_97byh */),
      'close',
      props,
      setHidden,
      props.closeButtonAction,
      props.closeButtonActionParams,
      ev.type
    )()
  } else {
    if (props.preventAutoDismiss) {
      if (ev && ev.type === 'click') {
        // log only the click event types to prevent over populating the data stream. For instance, a scroll event type generates many events.
        log.userActionEvent(
          0x23602886 /* tag_9yc8g */,
          'SurfaceDismissPrevented',
          {
            customData: {
              autoDismiss: true,
              eventType: 'click',
              surfaceType: props.surfaceType,
            },
          }
        )
      }

      return
    }

    handleClick(
      log.reserve(0x23841606 /* tag_97byg */),
      'auto dismiss',
      props,
      setHidden,
      props.closeButtonAction,
      props.closeButtonActionParams,
      ev && ev.type
    )()
  }
}

const onFocused = (startTime: number, onUpdated: () => void) => () => {
  log.userActionEvent(0x23602887 /* tag_9yc8h */, 'SurfaceFocused', {
    customData: {
      elapsedTime: Date.now() - startTime,
    },
  })

  onUpdated?.()
}

const onImageLoaded = (startTime: number) => (imageLoadedState: number) =>
  log.userActionEvent(0x23602888 /* tag_9yc8i */, 'ImageLoaded', {
    customData: {
      elapsedTime: Date.now() - startTime,
      imageLoadedState,
    },
  })

const onPrimaryButtonClick = (
  props: Props,
  setHidden: (hidden: boolean) => void
) =>
  handleClick(
    log.reserve(0x23841608 /* tag_97byi */),
    'primary',
    props,
    setHidden,
    props.primaryButtonAction,
    props.primaryButtonActionParams
  )

const onSecondaryButtonClick = (
  props: Props,
  setHidden: (hidden: boolean) => void
) =>
  handleClick(
    log.reserve(0x23841609 /* tag_97byj */),
    'secondary',
    props,
    setHidden,
    props.secondaryButtonAction,
    props.secondaryButtonActionParams
  )

export const getOfficeWebSurfaceContainer = (
  fabricDependencies: FabricDependencies
) => (props: Props) => {
  const {
    createElement: h,
    Customizer,
    useEffect,
    useMemo,
    useState,
  } = fabricDependencies

  const [hidden, setHidden] = useState(false)
  const [focused, setFocused] = useState(false)

  // tslint:disable-next-line: variable-name
  const Coachmark = useMemo(() => getCoachmarkSurface(fabricDependencies), [])

  // tslint:disable-next-line: variable-name
  const Teaching = useMemo(() => getTeachingSurface(fabricDependencies), [])

  useEffect(() => {
    // focus surface for keyboard a11y
    const onKeyboardEvent = (kbEvent: KeyboardEvent) => {
      if (
        !kbEvent ||
        // tslint:disable-next-line: deprecation
        kbEvent.keyCode === 9 || // tab key
        kbEvent.key === 'Tab' ||
        // tslint:disable-next-line: deprecation
        kbEvent.keyCode === 117 || // f6
        kbEvent.key === 'F6'
      ) {
        setFocused(true)
      }
    }

    document.addEventListener('keyup', onKeyboardEvent)

    return () => document.removeEventListener('keyup', onKeyboardEvent)
  }, [])

  if (hidden || !verifyProps(props)) {
    return null
  }

  const startTime = Date.now()

  const surfaceProps: SurfaceProps = {
    ...props,
    focused,
    onCalloutDismiss: onCalloutDismiss(props, setHidden),
    onFocused: onFocused(startTime, () => {
      setFocused(true)
      props.onUpdated?.()
    }),
    onImageLoaded: onImageLoaded(startTime),
    onPrimaryButtonClick: onPrimaryButtonClick(props, setHidden),
    onSecondaryButtonClick: onSecondaryButtonClick(props, setHidden),
  }

  const surface = (() => {
    switch (props.surfaceType) {
      case 'coachmark':
        return <Coachmark {...surfaceProps} />

      case 'teaching':
        return <Teaching {...surfaceProps} />

      default:
        log.error(
          0x23602889 /* tag_9yc8j */,
          'attempted to render unknown surface',
          props.surfaceType
        )

        return null
    }
  })()

  if (!surface) {
    return null
  }

  const customizer = overrideCustomizer(
    fabricDependencies,
    props.customTheme,
    props
  )

  return customizer ? (
    <Customizer {...customizer}>{surface}</Customizer>
  ) : (
    surface
  )
}
